import React, { ChangeEvent, useState } from 'react';
import Button, { ButtonGroup } from '@atlaskit/button';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import TextArea from '@atlaskit/textarea';

import {
    BULK_OPTIONS_INFO_TEXT as INFO_TEXT,
    BULK_OPTIONS_PLACEHOLDER_TEXT as PLACEHOLDER_TEXT,
    MINIMUM_ROWS,
    NEW_LINE_CHARACTER,
} from '../../utils/constants';

import { AddBulkOptionsProperties } from './interfaces';
import { AddBulkOptionsInfoText as InfoText, AddBulkOptionsWarningText as WarningText } from './styles';

const renderDuplicatesWarningText = (bulkDuplicates: string[], existingOptionsDuplicates: string[], dualDuplicates: string[]) => {
    if (dualDuplicates.length > 0) {
        const isMany = dualDuplicates.length > 1;

        return (
            <p>Remove option{isMany ? 's' : ''} that already exist{isMany ? '' : 's'} in the configured list: {dualDuplicates.join(', ')}</p>
        );
    } else if (bulkDuplicates.length > 0) {
        const isMany = bulkDuplicates.length > 1;

        return (
            <p>Remove duplicate option{isMany ? 's' : ''}: {bulkDuplicates.join(', ')}</p>
        );
    }

    const isMany = existingOptionsDuplicates.length > 1;

    return (
        <p>Remove option{isMany ? 's' : ''} that already exist{isMany ? '' : 's'} in the configured list: {existingOptionsDuplicates.join(', ')}</p>
    );
};

const AddBulkOptionsModal: React.FC<AddBulkOptionsProperties> = ({ onSubmit, onClose, currentOptions }) => {
    const [options, setOptions] = useState<string[]>([]);
    const [dualDuplicates, setDualDuplicates] = useState<string[]>([]);
    const [bulkDuplicates, setBulkDuplicates] = useState<string[]>([]);
    const [existingOptionsDuplicates, setExistingOptionsDuplicates] = useState<string[]>([]);

    const duplicatesExist = (): boolean => dualDuplicates.length > 0 || bulkDuplicates.length > 0 || existingOptionsDuplicates.length > 0;

    const getBulkDuplicatedOptions = (bulkOptions: string[]): string[] => bulkOptions.filter((option, i) => bulkOptions.indexOf(option) != i);

    const handleOptionsSubmit = () => {
        const localBulkDuplicates = getBulkDuplicatedOptions(options);
        const duplicatedValues = currentOptions.filter(option => options.includes(option));
        const duallyDuplicatedValues = duplicatedValues.filter(option => options.includes(option));

        if (duallyDuplicatedValues.length > 0) {
            setDualDuplicates(duallyDuplicatedValues);

            return;
        } else {
            setDualDuplicates([]);
        }

        if (localBulkDuplicates.length > 0) {
            setBulkDuplicates(localBulkDuplicates);

            return;
        } else {
            setBulkDuplicates([]);
        }

        setExistingOptionsDuplicates(duplicatedValues.length > 0
            ? duplicatedValues
            : []);
        duplicatedValues.length === 0 && onSubmit(options);
    };

    const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const optionsValue = event.currentTarget.value;
        const newOptions = optionsValue.includes(NEW_LINE_CHARACTER)
            ? optionsValue.split(NEW_LINE_CHARACTER).filter(option => option)
            : [optionsValue];
        setOptions(newOptions.map(option => option.trim()));
    };

    return (
        <Modal onClose={onClose}>
            <ModalHeader>
                <ModalTitle>Add options in bulk</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <TextArea
                    resize='auto'
                    name='add-bulk-options'
                    placeholder={PLACEHOLDER_TEXT}
                    onChange={handleInputChange}
                    minimumRows={MINIMUM_ROWS}
                />
                <InfoText>{INFO_TEXT}</InfoText>
                {duplicatesExist() && <WarningText>{renderDuplicatesWarningText(bulkDuplicates, existingOptionsDuplicates, dualDuplicates)}</WarningText>}
            </ModalBody>
            <ModalFooter>
                <ButtonGroup>
                    <Button onClick={onClose} appearance='subtle'>Cancel</Button>
                    <Button appearance='primary' onClick={handleOptionsSubmit}>Add</Button>
                </ButtonGroup>
            </ModalFooter>
        </Modal>
    );
};

export default AddBulkOptionsModal;
